import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  FaChevronLeft,
  FaEye,
  FaStore,
  FaCalendarDay,
  FaClock,
  FaTimes,
  FaHeadset,
  FaPiggyBank,
  FaCheck,
  FaTrophy,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaShoppingBag,
  FaTag,
  FaBox,
  FaCalendarCheck,
  FaMapMarker,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { GrServices } from "react-icons/gr";

const ShopOrder = () => {
  const navigate = useNavigate();
  const { OrderTotal } = useSelector((store) => store.Order);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const OrderStatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case "completed":
          return "success";
        case "confirmed":
          return "primary";
        case "pending":
          return "warning";
        case "cancelled":
          return "danger";
        default:
          return "secondary";
      }
    };

    const color = getStatusColor(status);

    return (
      <span className={`badge bg-${color} px-3 py-2 rounded-pill fw-semibold`}>
        {status || "Pending"}
      </span>
    );
  };

  const showOrderDetailsModal = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const OrderCard = ({ order }) => {
    const mainProduct = order.OrderProducts?.[0];
    const shopName =
      order.SalonName ||
      order.shopName ||
      mainProduct?.SalonName ||
      mainProduct?.shopName;

    return (
      <div
        className="bg-white rounded-3 shadow-sm mb-4 border border-light"
        style={{ cursor: "pointer" }}
        onClick={() => showOrderDetailsModal(order)}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1 me-3">
              <h5
                className="fw-bold text-dark mb-1"
                style={{ fontSize: "14px" }}
              >
                Shop Service Order
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                Order #{order._id?.slice(-8)?.toUpperCase()}
              </p>
            </div>
            <OrderStatusBadge status={order.CurrentStatus} />
          </div>

          <div className="mb-3" style={{ fontSize: "13px" }}>
            <div className="row g-3 mb-2">
              <div className="col-12 col-md-6 d-flex align-items-center">
                <FaCalendarDay className="text-muted me-2" />
                <span className="text-dark fw-medium">
                  {order.ServicePreferedDate || "Not scheduled"}
                </span>
              </div>
              <div className="col-12 col-md-6 d-flex align-items-center">
                <FaClock className="text-muted me-2" />
                <span className="text-dark fw-medium">
                  {order.ServicePreferedTime || "Not scheduled"}
                </span>
              </div>
            </div>

            {shopName && (
              <div className="d-flex align-items-center mb-3">
                <FaStore className="text-primary me-2" />
                <span className="fw-semibold text-primary">{shopName}</span>
              </div>
            )}
          </div>

          {mainProduct && (
            <div className="bg-light p-2 rounded-3 mb-3">
              <div className="d-flex align-items-center">
                <img
                  src={mainProduct.ImgUrl}
                  alt={mainProduct.ProductName}
                  className="rounded-2 me-3"
                  style={{ width: "70px", height: "70px", objectFit: "cover" }}
                />
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-1">{mainProduct.ProductName}</h6>
                  <p className="text-muted small mb-1">{mainProduct.Brand}</p>
                  {order.OrderProducts.length > 1 && (
                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1">
                      +{order.OrderProducts.length - 1} more services
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div
            className="d-flex justify-content-between align-items-center pt-3 border-top"
            style={{ fontSize: "12px" }}
          >
            <div className="text-muted">
              Booked on {moment(order.createdAt).format("DD MMM YYYY")}
            </div>
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                showOrderDetailsModal(order);
              }}
            >
              {/* <FaEye className="me-2" /> */}
              View Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  const OrderDetailsModal = () => {
    if (!selectedOrder) return null;

    const calculateTotalSavings = () => {
      if (!selectedOrder.OrderProducts) return 0;
      return selectedOrder.OrderProducts.reduce((total, product) => {
        return total + (product.Mrp - product.Price);
      }, 0);
    };

    const calculateTotalMrp = () => {
      if (!selectedOrder.OrderProducts) return 0;
      return selectedOrder.OrderProducts.reduce((total, product) => {
        return total + product.Mrp;
      }, 0);
    };

    const shopName =
      selectedOrder.SalonName ||
      selectedOrder.shopName ||
      selectedOrder.OrderProducts?.[0]?.SalonName ||
      selectedOrder.OrderProducts?.[0]?.shopName;

    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", marginTop: "30px" }}
        tabIndex="-1"
        onClick={() => setModalVisible(false)}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content rounded-4 border-0">
            {/* Modal Header */}
            <div className="modal-header border-bottom-0 p-4 pb-0">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h5 className="modal-title fw-bold fs-4">Order Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalVisible(false)}
                ></button>
              </div>
            </div>

            <div
              className="modal-body p-4"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Order Summary */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">Order Summary</h6>
                <div className="row g-3">
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Order ID</div>
                    <div className="fw-semibold">
                      {selectedOrder._id?.slice(-8)?.toUpperCase()}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Status</div>
                    <div>
                      <OrderStatusBadge status={selectedOrder.CurrentStatus} />
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Service Date</div>
                    <div className="fw-semibold">
                      {selectedOrder.ServicePreferedDate || "Not scheduled"}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Service Time</div>
                    <div className="fw-semibold">
                      {selectedOrder.ServicePreferedTime || "Not scheduled"}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Shop Name</div>
                    <div className="fw-semibold">{shopName || "N/A"}</div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Payment Mode</div>
                    <div className="fw-semibold">
                      {selectedOrder.PaymentMode || "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Details */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">
                  Services ({selectedOrder.OrderProducts?.length || 0})
                </h6>
                {selectedOrder.OrderProducts?.map((product, index) => (
                  <div key={index} className="bg-light rounded-3 p-3 mb-3">
                    <div className="d-flex">
                      <img
                        src={product.ImgUrl}
                        alt={product.ProductName}
                        className="rounded-2 me-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                        }}
                      />
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{product.ProductName}</h6>
                        <p className="text-muted small mb-2">{product.Brand}</p>

                        {(product.SalonName || product.shopName) && (
                          <div className="d-inline-flex align-items-center bg-primary bg-opacity-10 px-2 py-1 rounded-2 mb-2">
                            <FaStore size={12} className="text-primary me-1" />
                            <span className="text-primary fw-semibold small">
                              {product.SalonName || product.shopName}
                            </span>
                          </div>
                        )}

                        <div className="d-flex align-items-center mb-2">
                          <FaClock size={12} className="text-muted me-1" />
                          <span className="text-muted small">
                            {product.Duration}
                          </span>
                        </div>

                        {product.Services && product.Services.length > 0 && (
                          <div className="mb-2">
                            <p className="fw-semibold small mb-1">
                              Included Services:
                            </p>
                            <div className="row g-2">
                              {product.Services.map((service, serviceIndex) => (
                                <div
                                  key={serviceIndex}
                                  className="col-12 col-md-6 d-flex align-items-center"
                                >
                                  <FaCheck
                                    size={10}
                                    className="text-success me-2"
                                  />
                                  <span className="small">
                                    {service.service}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Price:</span>
                            <div className="d-flex align-items-center">
                              <span className="fw-bold fs-5">
                                ₹{product.Price}
                              </span>
                              {product.Mrp > product.Price && (
                                <span className="text-muted text-decoration-line-through ms-2">
                                  ₹{product.Mrp}
                                </span>
                              )}
                            </div>
                          </div>
                          {product.Mrp > product.Price && (
                            <div className="d-inline-flex align-items-center bg-success bg-opacity-10 px-3 py-1 rounded-2 mt-2">
                              <FaPiggyBank
                                size={12}
                                className="text-success me-2"
                              />
                              <span className="text-success fw-semibold small">
                                Save ₹{product.Mrp - product.Price}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">Price Breakdown</h6>
                <div className="bg-light p-4 rounded-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total MRP</span>
                    <span className="fw-semibold">₹{calculateTotalMrp()}</span>
                  </div>

                  {calculateTotalSavings() > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Discount</span>
                      <span className="fw-semibold text-success">
                        -₹{calculateTotalSavings()}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mb-2">
                    <span>Delivery Charge</span>
                    <span className="fw-semibold">
                      ₹{selectedOrder.DeliveryCharge || 0}
                    </span>
                  </div>

                  <hr className="my-3" />

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold fs-5">Grand Total</span>
                    <span className="fw-bold fs-4">
                      ₹{selectedOrder.GrandTotal || 0}
                    </span>
                  </div>

                  {calculateTotalSavings() > 0 && (
                    <div className="d-flex align-items-center bg-success bg-opacity-10 p-3 rounded-2 mt-3">
                      <FaTrophy className="text-success me-2" />
                      <span className="text-success fw-semibold">
                        You saved ₹{calculateTotalSavings()} on this order
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer & Shop Info */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">
                  Customer & Shop Information
                </h6>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <FaUser className="text-muted me-2" />
                      <div>
                        <div className="text-muted small">Customer Name</div>
                        <div className="fw-semibold">
                          {selectedOrder.ClientName}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <FaPhone className="text-muted me-2" />
                      <div>
                        <div className="text-muted small">Mobile</div>
                        <div className="fw-semibold">
                          {selectedOrder.Mobile}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-start mb-2">
                      <FaMapMarkerAlt className="text-muted me-2 mt-1" />
                      <div>
                        <div className="text-muted small">Service Address</div>
                        <div className="fw-semibold">
                          {selectedOrder.orderDisplayName ||
                            selectedOrder.Address}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <FaStore className="text-muted me-2" />
                      <div>
                        <div className="text-muted small">Shop Name</div>
                        <div className="fw-semibold">{shopName || "N/A"}</div>
                      </div>
                    </div>
                  </div>
                  {selectedOrder.shopAddress && (
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center mb-2">
                        <FaMapMarker className="text-muted me-2" />
                        <div>
                          <div className="text-muted small">Shop Address</div>
                          <div className="fw-semibold">
                            {selectedOrder.shopAddress}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Timeline */}
              <div>
                <h6 className="fw-bold mb-3 text-dark">Order Timeline</h6>
                <div className="ps-3 border-start border-2 border-primary">
                  <div className="mb-4 position-relative">
                    <div
                      className="position-absolute bg-primary rounded-circle"
                      style={{
                        width: "12px",
                        height: "12px",
                        left: "-18px",
                        top: "5px",
                      }}
                    ></div>
                    <div>
                      <div className="fw-semibold">Order Placed</div>
                      <div className="text-muted small">
                        {moment(selectedOrder.createdAt).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="position-relative">
                    <div
                      className="position-absolute bg-primary rounded-circle"
                      style={{
                        width: "12px",
                        height: "12px",
                        left: "-18px",
                        top: "5px",
                      }}
                    ></div>
                    <div>
                      <div className="fw-semibold">Scheduled Service</div>
                      <div className="text-muted small">
                        {selectedOrder.ServicePreferedDate} at{" "}
                        {selectedOrder.ServicePreferedTime}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer border-top-0 p-4 pt-0">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => setModalVisible(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
      <div
        className="bg-light rounded-circle d-flex align-items-center justify-content-center mb-4"
        style={{ width: "120px", height: "120px" }}
      >
        <FaShoppingBag size={48} className="text-muted" />
      </div>
      <h4 className="fw-bold mb-3">No Shop Orders</h4>
      <p className="text-muted text-center mb-4" style={{ maxWidth: "400px" }}>
        You haven't booked any shop services yet
      </p>
      <button
        className="btn btn-primary px-5 py-3 d-flex align-items-center"
        onClick={() => navigate("/")}
      >
        <GrServices className="me-2" />
        BOOK SERVICES
      </button>
    </div>
  );

  return (
    <div className="container p-2" style={{ marginTop: "80px" }}>
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center px-2 rounded-1 py-2 shadow-sm mb-3"
        style={{ backgroundColor: "#aa2b57" }}
      >
        <button
          className="btn btn-light btn-sm"
          onClick={() => navigate(-1)}
          style={{ minWidth: "40px" }}
        >
          <FaChevronLeft />
        </button>
        <h1
          className="text-white fs-6 fw-semibold m-0 text-center flex-grow-1"
          style={{ letterSpacing: "0.5px" }}
        >
          Shop Orders
        </h1>
      </div>

      {/* Main Content */}
      <div className="container py-4">
        {OrderTotal?.length > 0 ? (
          <div className="row">
            {OrderTotal.map((order, index) => (
              <div
                key={order._id || index}
                className="col-12 col-lg-6 col-xl-4 mb-3"
              >
                <OrderCard order={order} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Modal */}
      {modalVisible && <OrderDetailsModal />}
    </div>
  );
};

export default ShopOrder;
